import { useEffect, useState } from "react";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

// Pre-filled rather than a separate ISP-settings field -- this ISP operates
// in one country, so defaulting it here means it almost never needs to be
// typed or changed, without building a whole settings page for one value.
const DEFAULT_COUNTRY = "Kenya";
const empty = { country: DEFAULT_COUNTRY, city: "", suburb: "", zip_code: "" };

export default function PppoeLocations() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/pppoe/locations");
      setItems(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (l) => {
    setEditing(l.id);
    setForm({ country: l.country, city: l.city, suburb: l.suburb || "", zip_code: l.zip_code || "" });
    setOpen(true);
  };

  const submit = async () => {
    if (!form.country || !form.city) {
      toast.error("Country and city are required");
      return;
    }
    try {
      if (editing) await api.put(`/admin/pppoe/locations/${editing}`, form);
      else await api.post("/admin/pppoe/locations", form);
      toast.success(editing ? "Location updated" : "Location created");
      setOpen(false);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this location? Any customer assigned to it will be unassigned.")) return;
    try {
      await api.delete(`/admin/pppoe/locations/${id}`);
      toast.success("Deleted");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div>
      <PageHeader
        testId="pppoe-locations-header"
        title="Locations"
        subtitle="Country / city / suburb / zip — assignable to any customer's profile"
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button
                data-testid="new-pppoe-location-btn"
                onClick={openNew}
                className="h-10 rounded-none bg-nhp-primary hover:bg-nhp-primary-hover text-nhp-on-primary font-bold"
              >
                <Plus className="h-4 w-4 mr-1" /> NEW LOCATION
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-nhp-surface border-nhp-border text-nhp-text rounded-none max-w-md">
              <DialogHeader>
                <DialogTitle className="font-heading">{editing ? "Edit Location" : "New Location"}</DialogTitle>
                <DialogDescription className="text-nhp-muted">
                  Country, city, suburb, and postal/zip code — in that order.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-2">
                <div>
                  <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-nhp-muted">Country</Label>
                  <Input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} className="mt-1 bg-nhp-bg border-nhp-border rounded-none h-10" />
                </div>
                <div>
                  <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-nhp-muted">City</Label>
                  <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="mt-1 bg-nhp-bg border-nhp-border rounded-none h-10" />
                </div>
                <div>
                  <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-nhp-muted">Suburb (optional)</Label>
                  <Input value={form.suburb} onChange={(e) => setForm({ ...form, suburb: e.target.value })} className="mt-1 bg-nhp-bg border-nhp-border rounded-none h-10" />
                </div>
                <div>
                  <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-nhp-muted">Zip / Postal code (optional)</Label>
                  <Input value={form.zip_code} onChange={(e) => setForm({ ...form, zip_code: e.target.value })} className="mt-1 bg-nhp-bg border-nhp-border rounded-none h-10 font-mono" />
                </div>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setOpen(false)} className="rounded-none">Cancel</Button>
                <Button onClick={submit} className="rounded-none bg-nhp-primary hover:bg-nhp-primary-hover text-nhp-on-primary font-bold">SAVE</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="p-4 md:p-8">
        <div className="border border-nhp-border bg-nhp-surface">
          <div className="hidden md:grid grid-cols-12 px-6 py-3 border-b border-nhp-border text-[10px] uppercase tracking-[0.2em] text-nhp-muted font-mono">
            <div className="col-span-3">Country</div>
            <div className="col-span-3">City</div>
            <div className="col-span-3">Suburb</div>
            <div className="col-span-2">Zip / Postal</div>
            <div className="col-span-1 text-right">Actions</div>
          </div>
          <div className="divide-y divide-nhp-border">
            {items.length === 0 && (
              <div className="p-8 text-center text-sm text-nhp-muted">No locations yet.</div>
            )}
            {items.map((l) => (
              <div key={l.id} className="flex flex-col gap-2 md:grid md:grid-cols-12 px-4 md:px-6 py-4 md:items-center hover:bg-nhp-bg">
                <div className="md:col-span-3">{l.country}</div>
                <div className="md:col-span-3">{l.city}</div>
                <div className="md:col-span-3 text-nhp-muted">{l.suburb || "—"}</div>
                <div className="font-mono md:col-span-2">{l.zip_code || "—"}</div>
                <div className="flex items-center gap-2 md:col-span-1 md:justify-end">
                  <Button variant="ghost" size="sm" onClick={() => openEdit(l)} className="h-8 rounded-none text-nhp-muted hover:text-nhp-text">
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => remove(l.id)} className="h-8 rounded-none text-nhp-muted hover:text-nhp-critical">
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
